import { Student } from "../context/AuthContext";

// ── Applications ──────────────────────────────────────────────────────────────
export type AppStatus =
  | "draft"
  | "submitted"
  | "under_review"
  | "offer_received"
  | "accepted"
  | "rejected";

export interface Application {
  id: string;
  university: string;
  course: string;
  country: string;
  flag: string;
  type: "local" | "overseas";
  status: AppStatus;
  appliedAt: string;
  deadline: string;
  tuition: string;
  scholarshipAmount: string;
  intake: string;
  completedDocs: number;
  totalDocs: number;
  notes?: string;
}

export const MOCK_APPLICATIONS: Application[] = [
  {
    id: "app_001",
    university: "Taylor's University",
    course: "Bachelor of Medicine & Surgery (MBBS)",
    country: "Malaysia",
    flag: "🇲🇾",
    type: "local",
    status: "offer_received",
    appliedAt: "10 Jan 2025",
    deadline: "1 Mar 2025",
    tuition: "RM 380,000 total",
    scholarshipAmount: "RM 38,000 (10%)",
    intake: "March 2025",
    completedDocs: 4,
    totalDocs: 4,
    notes: "Counselor negotiating for a higher scholarship — update by EOD.",
  },
  {
    id: "app_002",
    university: "UCSI University",
    course: "Bachelor of Medicine & Surgery (MBBS)",
    country: "Malaysia",
    flag: "🇲🇾",
    type: "local",
    status: "under_review",
    appliedAt: "15 Jan 2025",
    deadline: "15 Mar 2025",
    tuition: "RM 350,000 total",
    scholarshipAmount: "Pending assessment",
    intake: "March 2025",
    completedDocs: 3,
    totalDocs: 4,
  },
  {
    id: "app_003",
    university: "International Medical University (IMU)",
    course: "Bachelor of Medicine & Surgery (MBBS)",
    country: "Malaysia",
    flag: "🇲🇾",
    type: "local",
    status: "submitted",
    appliedAt: "20 Jan 2025",
    deadline: "1 Apr 2025",
    tuition: "RM 390,000 total",
    scholarshipAmount: "—",
    intake: "September 2025",
    completedDocs: 2,
    totalDocs: 5,
  },
  {
    id: "app_004",
    university: "University of Melbourne",
    course: "Bachelor of Biomedicine",
    country: "Australia",
    flag: "🇦🇺",
    type: "overseas",
    status: "draft",
    appliedAt: "—",
    deadline: "30 Jun 2025",
    tuition: "AUD 48,000 / year",
    scholarshipAmount: "Scholarship search in progress",
    intake: "February 2026",
    completedDocs: 0,
    totalDocs: 6,
  },
];

// ── Scholarships ───────────────────────────────────────────────────────────────
export interface Scholarship {
  id: string;
  name: string;
  provider: string;
  type: "Government" | "Corporate" | "University" | "Private";
  amount: string;
  deadline: string;
  match: number;
  fields: string[];
  link: string;
  saved: boolean;
}

export const MOCK_SCHOLARSHIPS: Scholarship[] = [
  {
    id: "sch_001",
    name: "Yayasan Tenaga Nasional (YTN) Scholarship",
    provider: "Tenaga Nasional Berhad",
    type: "Corporate",
    amount: "Full tuition + RM 1,200/month allowance",
    deadline: "31 Mar 2025",
    match: 92,
    fields: ["Engineering", "IT", "Science", "Medicine"],
    link: "#",
    saved: true,
  },
  {
    id: "sch_002",
    name: "Petronas Education Sponsorship Programme",
    provider: "Petronas",
    type: "Corporate",
    amount: "Full tuition + RM 1,500/month + laptop",
    deadline: "15 Apr 2025",
    match: 87,
    fields: ["Engineering", "Science", "Business"],
    link: "#",
    saved: false,
  },
  {
    id: "sch_003",
    name: "Taylor's Excellence Award",
    provider: "Taylor's University",
    type: "University",
    amount: "Up to 100% tuition waiver",
    deadline: "28 Feb 2025",
    match: 78,
    fields: ["All fields"],
    link: "#",
    saved: true,
  },
  {
    id: "sch_004",
    name: "Bank Negara KIJANG Emas Scholarship",
    provider: "Bank Negara Malaysia",
    type: "Government",
    amount: "Full scholarship + overseas postgraduate",
    deadline: "1 May 2025",
    match: 71,
    fields: ["Economics", "Finance", "Law", "IT"],
    link: "#",
    saved: false,
  },
  {
    id: "sch_005",
    name: "JPA Public Service Department Scholarship",
    provider: "Jabatan Perkhidmatan Awam",
    type: "Government",
    amount: "Full ride – local & overseas",
    deadline: "15 May 2025",
    match: 68,
    fields: ["Medicine", "Engineering", "Law", "Architecture"],
    link: "#",
    saved: false,
  },
];

// ── Partner Perks ──────────────────────────────────────────────────────────────
export interface Perk {
  id: string;
  brand: string;
  deal: string;
  category: string;
  emoji: string;
  pointsCost: number | null;
  expiresAt: string;
  redeemed: boolean;
}

export const MOCK_PERKS: Perk[] = [
  {
    id: "perk_001",
    brand: "Boost e-Wallet",
    deal: "RM50 cashback on first reload ≥ RM100",
    category: "Financial",
    emoji: "💳",
    pointsCost: 500,
    expiresAt: "30 Apr 2025",
    redeemed: false,
  },
  {
    id: "perk_002",
    brand: "Grab",
    deal: "5× GrabPoints on all rides for 30 days",
    category: "Transport",
    emoji: "🚗",
    pointsCost: 300,
    expiresAt: "28 Feb 2025",
    redeemed: true,
  },
  {
    id: "perk_003",
    brand: "Popular Bookstore",
    deal: "15% off all textbooks & stationery",
    category: "Education",
    emoji: "📚",
    pointsCost: 200,
    expiresAt: "31 Dec 2025",
    redeemed: false,
  },
  {
    id: "perk_004",
    brand: "Uniqlo Malaysia",
    deal: "10% student discount in-store & online",
    category: "Lifestyle",
    emoji: "👕",
    pointsCost: null,
    expiresAt: "31 Dec 2025",
    redeemed: false,
  },
  {
    id: "perk_005",
    brand: "McDonald's",
    deal: "Student Meal at RM 6.90 (any main + drink)",
    category: "F&B",
    emoji: "🍔",
    pointsCost: null,
    expiresAt: "31 Dec 2025",
    redeemed: false,
  },
  {
    id: "perk_006",
    brand: "CelcomDigi",
    deal: "Free 10GB data/month for students",
    category: "Telco",
    emoji: "📱",
    pointsCost: 150,
    expiresAt: "30 Jun 2025",
    redeemed: false,
  },
  {
    id: "perk_007",
    brand: "Spotify Premium",
    deal: "3 months free (student plan)",
    category: "Lifestyle",
    emoji: "🎵",
    pointsCost: 400,
    expiresAt: "31 Mar 2025",
    redeemed: false,
  },
  {
    id: "perk_008",
    brand: "Shopee",
    deal: "RM20 voucher bundle on education items",
    category: "Shopping",
    emoji: "🛍️",
    pointsCost: 250,
    expiresAt: "28 Feb 2025",
    redeemed: false,
  },
];

// ── Chat Messages ──────────────────────────────────────────────────────────────
export interface ChatMessage {
  id: string;
  from: "counselor" | "student";
  text: string;
  time: string;
  date: string;
  read: boolean;
  attachment?: { name: string; type: "pdf" | "image" };
}

export const MOCK_MESSAGES: ChatMessage[] = [
  {
    id: "msg_001",
    from: "counselor",
    text: "Hi Ain! Welcome to SCS Education 🎉 I'm Kak Sarah, your dedicated counselor. I'll be guiding you through your entire university application journey. Feel free to text me anytime!",
    time: "9:00 AM",
    date: "15 Nov 2024",
    read: true,
  },
  {
    id: "msg_002",
    from: "student",
    text: "Hi Kak Sarah! So excited to get started. I really want to study Medicine. Is that realistic with my results?",
    time: "9:15 AM",
    date: "15 Nov 2024",
    read: true,
  },
  {
    id: "msg_003",
    from: "counselor",
    text: "Absolutely! Your SPM results are outstanding — 9A+/A. You're a strong candidate for MBBS locally. I've shortlisted 4 universities that match your profile and budget perfectly 🌟",
    time: "9:20 AM",
    date: "15 Nov 2024",
    read: true,
  },
  {
    id: "msg_004",
    from: "counselor",
    text: "I've submitted your profile to Taylor's University, UCSI, and IMU. You should expect acknowledgement emails within 3-5 working days.",
    time: "2:30 PM",
    date: "10 Jan 2025",
    read: true,
  },
  {
    id: "msg_005",
    from: "student",
    text: "Amazing! Should I be doing anything on my end while we wait?",
    time: "2:45 PM",
    date: "10 Jan 2025",
    read: true,
  },
  {
    id: "msg_006",
    from: "counselor",
    text: "Yes! Please upload your Recommendation Letter to the Documents section. UCSI specifically requires it before March 15. I'll send you a template 📄",
    time: "2:50 PM",
    date: "10 Jan 2025",
    read: true,
    attachment: { name: "Recommendation_Letter_Template.pdf", type: "pdf" },
  },
  {
    id: "msg_007",
    from: "student",
    text: "Got it, uploading now! Thank you!",
    time: "3:05 PM",
    date: "10 Jan 2025",
    read: true,
  },
  {
    id: "msg_008",
    from: "counselor",
    text: "Hi Ain! Great news — Taylor's has reviewed your application and they're offering you a 10% scholarship 🎉 The official offer letter will arrive in your email within 3 days!",
    time: "9:15 AM",
    date: "20 Jan 2025",
    read: true,
  },
  {
    id: "msg_009",
    from: "student",
    text: "OH MY GOD! That's amazing!! Can we negotiate for a higher amount?",
    time: "9:30 AM",
    date: "20 Jan 2025",
    read: true,
  },
  {
    id: "msg_010",
    from: "counselor",
    text: "Yes definitely! I've done this many times. I'll set up a negotiation call with their admissions office — usually we can push another 5-10%. I'll update you by this evening 😊",
    time: "9:35 AM",
    date: "20 Jan 2025",
    read: true,
  },
  {
    id: "msg_011",
    from: "counselor",
    text: "Also, shall we also start your University of Melbourne application? Biomedicine there is a fantastic backup option, and the Feb 2026 intake deadline gives us time to prep a strong application.",
    time: "10:05 AM",
    date: "21 Jan 2025",
    read: false,
  },
];

// ── Documents ─────────────────────────────────────────────────────────────────
export type DocStatus = "verified" | "pending" | "needed";

export interface Document {
  id: string;
  name: string;
  category: "Identity" | "Academic" | "Financial" | "Others";
  status: DocStatus;
  uploadedAt: string | null;
  size: string | null;
  required: boolean;
}

export const MOCK_DOCUMENTS: Document[] = [
  {
    id: "doc_001",
    name: "MyKad (National IC)",
    category: "Identity",
    status: "verified",
    uploadedAt: "5 Jan 2025",
    size: "2.1 MB",
    required: true,
  },
  {
    id: "doc_002",
    name: "Passport",
    category: "Identity",
    status: "verified",
    uploadedAt: "5 Jan 2025",
    size: "3.4 MB",
    required: true,
  },
  {
    id: "doc_003",
    name: "SPM Certificate",
    category: "Academic",
    status: "verified",
    uploadedAt: "6 Jan 2025",
    size: "4.2 MB",
    required: true,
  },
  {
    id: "doc_004",
    name: "SPM Slip / Transcript",
    category: "Academic",
    status: "pending",
    uploadedAt: "6 Jan 2025",
    size: "1.8 MB",
    required: true,
  },
  {
    id: "doc_005",
    name: "School Leaving Certificate",
    category: "Academic",
    status: "verified",
    uploadedAt: "7 Jan 2025",
    size: "2.5 MB",
    required: true,
  },
  {
    id: "doc_006",
    name: "Recommendation Letter",
    category: "Academic",
    status: "needed",
    uploadedAt: null,
    size: null,
    required: true,
  },
  {
    id: "doc_007",
    name: "Parent's Income Statement",
    category: "Financial",
    status: "pending",
    uploadedAt: "10 Jan 2025",
    size: "890 KB",
    required: false,
  },
  {
    id: "doc_008",
    name: "Bank Statement (3 months)",
    category: "Financial",
    status: "needed",
    uploadedAt: null,
    size: null,
    required: false,
  },
  {
    id: "doc_009",
    name: "Personal Statement",
    category: "Others",
    status: "verified",
    uploadedAt: "12 Jan 2025",
    size: "128 KB",
    required: true,
  },
  {
    id: "doc_010",
    name: "Extracurricular Certificates",
    category: "Others",
    status: "pending",
    uploadedAt: "13 Jan 2025",
    size: "5.1 MB",
    required: false,
  },
];

// ── Explore – Universities ────────────────────────────────────────────────────
export interface University {
  id: string;
  slug: string;
  name: string;
  country: string;
  flag: string;
  fields: string[];
  tuition: string;
  intake: string;
  ranking: string;
  type: "local" | "overseas";
  saved: boolean;
  logo: string;
  rating: number;
}

export const MOCK_UNIVERSITIES: University[] = [
  {
    id: "uni_001",
    slug: "universiti-malaya",
    name: "Universiti Malaya (UM)",
    country: "Malaysia",
    flag: "🇲🇾",
    fields: ["Medicine", "Engineering", "Law", "Business"],
    tuition: "RM 3,000–RM 18,000/yr",
    intake: "March & September",
    ranking: "#1 Malaysia • #65 QS Asia",
    type: "local",
    saved: false,
    logo: "UM",
    rating: 4.8,
  },
  {
    id: "uni_002",
    slug: "taylors-university",
    name: "Taylor's University",
    country: "Malaysia",
    flag: "🇲🇾",
    fields: ["Medicine", "Business", "Design", "Computing"],
    tuition: "RM 18,000–RM 65,000/yr",
    intake: "Feb, Jul & Oct",
    ranking: "#2 Private MY • Top 1% World",
    type: "local",
    saved: true,
    logo: "TU",
    rating: 4.7,
  },
  {
    id: "uni_003",
    slug: "monash-university-malaysia",
    name: "Monash University Malaysia",
    country: "Malaysia",
    flag: "🇲🇾",
    fields: ["Engineering", "IT", "Pharmacy", "Business"],
    tuition: "RM 32,000–RM 65,000/yr",
    intake: "February & July",
    ranking: "#29 QS Asia",
    type: "local",
    saved: false,
    logo: "MU",
    rating: 4.6,
  },
  {
    id: "uni_004",
    slug: "university-of-nottingham-malaysia",
    name: "University of Nottingham MY",
    country: "Malaysia",
    flag: "🇲🇾",
    fields: ["Engineering", "CS", "Pharmacy", "Law"],
    tuition: "RM 28,000–RM 58,000/yr",
    intake: "September",
    ranking: "#3 Research MY",
    type: "local",
    saved: false,
    logo: "NU",
    rating: 4.5,
  },
  {
    id: "uni_005",
    slug: "ucsi-university",
    name: "UCSI University",
    country: "Malaysia",
    flag: "🇲🇾",
    fields: ["Medicine", "Nursing", "Music", "Architecture"],
    tuition: "RM 15,000–RM 45,000/yr",
    intake: "Jan, May & Sept",
    ranking: "Top 10 Private MY",
    type: "local",
    saved: true,
    logo: "UC",
    rating: 4.3,
  },
  {
    id: "uni_006",
    slug: "university-of-melbourne",
    name: "University of Melbourne",
    country: "Australia",
    flag: "🇦🇺",
    fields: ["Biomedicine", "Engineering", "Arts", "Law"],
    tuition: "AUD 42,000–AUD 55,000/yr",
    intake: "February & July",
    ranking: "#33 QS World",
    type: "overseas",
    saved: true,
    logo: "UM",
    rating: 4.9,
  },
  {
    id: "uni_007",
    slug: "bond-university",
    name: "Bond University",
    country: "Australia",
    flag: "🇦🇺",
    fields: ["Law", "Business", "IT", "Film & TV"],
    tuition: "AUD 29,000–AUD 42,000/yr",
    intake: "Jan, May & Sept",
    ranking: "#1 Student Experience AU",
    type: "overseas",
    saved: false,
    logo: "BU",
    rating: 4.6,
  },
  {
    id: "uni_008",
    slug: "university-of-leeds",
    name: "University of Leeds",
    country: "United Kingdom",
    flag: "🇬🇧",
    fields: ["Engineering", "Arts", "Medicine", "Science"],
    tuition: "GBP 18,000–GBP 26,000/yr",
    intake: "September",
    ranking: "#100 QS World",
    type: "overseas",
    saved: false,
    logo: "UL",
    rating: 4.5,
  },
  {
    id: "uni_009",
    slug: "nus-singapore",
    name: "NUS Singapore",
    country: "Singapore",
    flag: "🇸🇬",
    fields: ["Computing", "Business", "Medicine", "Engineering"],
    tuition: "SGD 17,550–SGD 37,350/yr",
    intake: "August",
    ranking: "#8 QS World",
    type: "overseas",
    saved: false,
    logo: "NS",
    rating: 4.9,
  },
  {
    id: "uni_010",
    slug: "deakin-university",
    name: "Deakin University",
    country: "Australia",
    flag: "🇦🇺",
    fields: ["Nursing", "IT", "Business", "Education"],
    tuition: "AUD 26,000–AUD 37,000/yr",
    intake: "March & July",
    ranking: "Top 2% World",
    type: "overseas",
    saved: false,
    logo: "DU",
    rating: 4.4,
  },
];

// ── Deadlines ─────────────────────────────────────────────────────────────────
export interface Deadline {
  id: string;
  label: string;
  date: string;
  daysLeft: number;
  type: "application" | "document" | "scholarship" | "intake";
  urgent: boolean;
}

export const MOCK_DEADLINES: Deadline[] = [
  {
    id: "dl_001",
    label: "Taylor's — Accept Offer",
    date: "15 Feb 2025",
    daysLeft: 8,
    type: "application",
    urgent: true,
  },
  {
    id: "dl_002",
    label: "UCSI — Recommendation Letter",
    date: "15 Mar 2025",
    daysLeft: 36,
    type: "document",
    urgent: false,
  },
  {
    id: "dl_003",
    label: "Taylor's Excellence Award",
    date: "28 Feb 2025",
    daysLeft: 21,
    type: "scholarship",
    urgent: true,
  },
  {
    id: "dl_004",
    label: "IMU — Remaining Documents",
    date: "1 Apr 2025",
    daysLeft: 53,
    type: "document",
    urgent: false,
  },
  {
    id: "dl_005",
    label: "Melbourne — Application Opens",
    date: "1 Mar 2025",
    daysLeft: 22,
    type: "application",
    urgent: false,
  },
];

export const MOCK_STUDENT: Student = {
  id: "student_001",
  name: "Ain Nabila binti Ahmad",
  email: "ain@scseducation.com",
  phoneNumber: "+60 12-345 6789",
  ic: "060101-14-1234",
  dob: "2006-01-01",
  gender: "Female",
  nationality: "Malaysian",
  state: "Kuala Lumpur",
  school: "SMK Taman Melawati, Kuala Lumpur",
  spmYear: 2023,
  spmGrades: [
    { subject: "Bahasa Melayu", grade: "A+" },
    { subject: "English Language", grade: "A+" },
    { subject: "Mathematics", grade: "A+" },
    { subject: "Additional Mathematics", grade: "A" },
    { subject: "Biology", grade: "A+" },
    { subject: "Chemistry", grade: "A" },
    { subject: "Physics", grade: "A" },
    { subject: "History", grade: "A" },
    { subject: "Pendidikan Islam", grade: "A+" },
  ],
  pathway: "both",
  interests: ["Medicine", "Biotechnology", "Life Sciences"],
  budget: "RM 20,000 – RM 40,000 / year",
  avatarColor: "#7C3AED",
  points: 2450,
  tier: "Silver",
  joinedAt: "2024-11-15",
  counselorId: "counselor_001",
  profileCompletion: 85,
};

export interface UniProgram {
  name: string;
  duration: string;
  tuition: string;
  intakes: string[];
}

export interface UniDetail {
  id: string;
  slug: string;
  about: string;
  location: string;
  website: string;
  established: string;
  totalStudents: string;
  internationalStudents: string;
  accentColor: string;
  bgGradient: string;
  highlights: string[];
  programs: UniProgram[];
  entryRequirements: string[];
  scholarships: { name: string; amount: string; criteria: string }[];
  scsRecommendReasons: string[];
  campusLife: string[];
}

export const UNIVERSITY_DETAILS: Record<string, UniDetail> = {
  "taylors-university": {
    id: "uni_002",
    slug: "taylors-university",
    about:
      "Taylor's University is Malaysia's top-ranked private university, consistently recognised for academic excellence and strong graduate employability. With state-of-the-art campuses and partnerships with 200+ global universities, Taylor's offers a truly international education experience right here in Malaysia.",
    location: "Subang Jaya, Selangor, Malaysia",
    website: "www.taylors.edu.my",
    established: "1969",
    totalStudents: "21,000+",
    internationalStudents: "5,000+",
    accentColor: "#F97316",
    bgGradient: "from-orange-500 to-amber-400",
    highlights: [
      "QS World University Rankings Top 1%",
      "#2 Private University in Malaysia",
      "94% Graduate Employment Rate",
      "200+ Global University Partners",
      "AACSB & AMBA Accredited Business School",
    ],
    programs: [
      {
        name: "Bachelor of Medicine & Surgery (MBBS)",
        duration: "5 years",
        tuition: "RM 380,000 total",
        intakes: ["March", "September"],
      },
      {
        name: "Bachelor of Business (Hons)",
        duration: "3 years",
        tuition: "RM 75,000 total",
        intakes: ["February", "July", "October"],
      },
      {
        name: "Bachelor of Computer Science (Hons)",
        duration: "3 years",
        tuition: "RM 68,000 total",
        intakes: ["February", "July", "October"],
      },
      {
        name: "Bachelor of Architecture (Hons)",
        duration: "4 years",
        tuition: "RM 112,000 total",
        intakes: ["February", "July"],
      },
      {
        name: "Foundation in Science",
        duration: "1 year",
        tuition: "RM 22,000 total",
        intakes: ["February", "July", "October"],
      },
    ],
    entryRequirements: [
      "SPM: Minimum 5Bs (subject-specific for Medicine: 8A+/A)",
      "IGCSE/O-Level: Minimum 5Bs",
      "Foundation/Matriculation: CGPA 2.5+ (Medicine: 3.8+)",
      "English: IELTS 5.5+ or equivalent (MBBS: 6.5+)",
      "Relevant subjects: Biology & Chemistry for Medical programmes",
    ],
    scholarships: [
      {
        name: "Taylor's Excellence Award",
        amount: "Up to 100% tuition waiver",
        criteria: "SPM 9A+ or equivalent",
      },
      {
        name: "Taylor's Merit Award",
        amount: "10–50% tuition discount",
        criteria: "SPM 5A+ or equivalent",
      },
      {
        name: "SCS Partner Scholarship",
        amount: "Additional 5–10% via SCS negotiation",
        criteria: "SCS Education enrolled students",
      },
    ],
    scsRecommendReasons: [
      "Strong match with your academic profile and target field",
      "SCS has successfully placed 40+ students here since 2020",
      "Our counsellors have a direct relationship with Taylor's admissions team",
      "Scholarship negotiation track record: avg 15% additional discount secured",
      "Flexible intake dates fit your timeline",
    ],
    campusLife: [
      "Modern 27-acre lakeside campus",
      "On-campus Student Village & residences",
      "100+ student clubs & societies",
      "World-class sports facilities",
      "Cafes, food courts & retail outlets on campus",
    ],
  },
  "ucsi-university": {
    id: "uni_005",
    slug: "ucsi-university",
    about:
      "UCSI University is a pioneer in private higher education in Malaysia, celebrated for its innovative problem-based learning and strong ties to healthcare and creative industries. With over 100 programmes and students from 115+ nations, UCSI offers a diverse, globally-oriented campus community in the heart of Kuala Lumpur.",
    location: "Cheras, Kuala Lumpur, Malaysia",
    website: "www.ucsiuniversity.edu.my",
    established: "1986",
    totalStudents: "14,000+",
    internationalStudents: "4,200+",
    accentColor: "#EC4899",
    bgGradient: "from-pink-500 to-rose-400",
    highlights: [
      "Top 10 Private Universities Malaysia",
      "115+ Nationalities on Campus",
      "Problem-Based Learning Pioneer",
      "Award-winning Music Conservatory",
      "Strong Medical & Nursing Programmes",
    ],
    programs: [
      {
        name: "Bachelor of Medicine & Surgery (MBBS)",
        duration: "5 years",
        tuition: "RM 350,000 total",
        intakes: ["January", "May", "September"],
      },
      {
        name: "Bachelor of Nursing (Hons)",
        duration: "3 years",
        tuition: "RM 55,000 total",
        intakes: ["January", "May", "September"],
      },
      {
        name: "Bachelor of Music (Hons)",
        duration: "3 years",
        tuition: "RM 62,000 total",
        intakes: ["January", "September"],
      },
      {
        name: "Bachelor of Architecture (Hons)",
        duration: "4 years",
        tuition: "RM 95,000 total",
        intakes: ["January", "September"],
      },
      {
        name: "Bachelor of Pharmacy (Hons)",
        duration: "4 years",
        tuition: "RM 120,000 total",
        intakes: ["January", "September"],
      },
    ],
    entryRequirements: [
      "SPM: Minimum 5Cs including English (Medicine: 8A+/A)",
      "STPM: Minimum 2 principal passes",
      "Foundation/Matriculation: CGPA 2.0+ (Medicine: 3.75+)",
      "English: IELTS 5.5+ (Medicine: 6.5+)",
      "Interview required for Medicine, Nursing & Music programmes",
    ],
    scholarships: [
      {
        name: "UCSI Merit Scholarship",
        amount: "25–50% tuition reduction",
        criteria: "SPM 7A+ or equivalent",
      },
      {
        name: "UCSI Vice Chancellor Award",
        amount: "Full tuition waiver",
        criteria: "Exceptional academic + co-curricular achievement",
      },
      {
        name: "SCS Partner Scholarship",
        amount: "Additional 5–10% via SCS negotiation",
        criteria: "SCS Education enrolled students",
      },
    ],
    scsRecommendReasons: [
      "Excellent choice for Medicine, Nursing & health sciences",
      "More flexible intake dates than most local universities",
      "Strong scholarship availability for SCS-referred students",
      "SCS counsellors have direct contacts in UCSI admissions",
      "Lower tuition fees compared to Taylor's — great value for MBBS",
    ],
    campusLife: [
      "Vibrant city-centre campus in Cheras",
      "On-campus hostel with modern facilities",
      "Active student union with 80+ clubs",
      "Cultural festivals celebrating 115+ nationalities",
      "Dedicated music performance halls",
    ],
  },
  "asia-pacific-university": {
    id: "uni_011",
    slug: "asia-pacific-university",
    about:
      "Asia Pacific University (APU) is Malaysia's leading technology-focused university, ranked among the top IT institutions in Asia. APU's industry-driven curriculum, delivered in partnership with global tech giants, ensures graduates are job-ready from day one. Its vibrant international community and modern Tech Hub campus make it a top choice for future-forward students.",
    location: "Technology Park Malaysia, Kuala Lumpur",
    website: "www.apu.edu.my",
    established: "1993",
    totalStudents: "12,000+",
    internationalStudents: "3,800+",
    accentColor: "#7C3AED",
    bgGradient: "from-violet-600 to-purple-500",
    highlights: [
      "#1 Technology University in Malaysia",
      "Top 50 Asia IT Rankings",
      "Industry Partners: Microsoft, Oracle, IBM",
      "98% Graduate Employment in 6 Months",
      "Students from 130+ Countries",
    ],
    programs: [
      {
        name: "BSc (Hons) Computer Science",
        duration: "3 years",
        tuition: "RM 48,000 total",
        intakes: ["January", "May", "September"],
      },
      {
        name: "BSc (Hons) Artificial Intelligence",
        duration: "3 years",
        tuition: "RM 52,000 total",
        intakes: ["January", "May", "September"],
      },
      {
        name: "BSc (Hons) Cyber Security",
        duration: "3 years",
        tuition: "RM 50,000 total",
        intakes: ["January", "September"],
      },
      {
        name: "BEng (Hons) Electrical & Electronic Engineering",
        duration: "4 years",
        tuition: "RM 65,000 total",
        intakes: ["January", "September"],
      },
      {
        name: "BBA (Hons) Business Management",
        duration: "3 years",
        tuition: "RM 42,000 total",
        intakes: ["January", "May", "September"],
      },
    ],
    entryRequirements: [
      "SPM: Minimum 5Cs including Mathematics & English",
      "IGCSE/O-Level: Minimum 5Cs with relevant subjects",
      "Foundation/Pre-U: CGPA 2.0+ (Engineering: 2.5+)",
      "English: IELTS 5.5+ or equivalent",
      "Strong aptitude in Mathematics recommended for Engineering & Computing",
    ],
    scholarships: [
      {
        name: "APU Academic Excellence Award",
        amount: "Up to 50% tuition waiver",
        criteria: "SPM 8A+ or equivalent",
      },
      {
        name: "APU Sports & Leadership Award",
        amount: "Up to 25% tuition reduction",
        criteria: "Exceptional co-curricular achievement",
      },
      {
        name: "SCS Partner Scholarship",
        amount: "Additional 5–10% via SCS negotiation",
        criteria: "SCS Education enrolled students",
      },
    ],
    scsRecommendReasons: [
      "Best-in-class for technology and computing courses in Malaysia",
      "Strong industry connections mean better internship and job prospects",
      "Counsellor check recommended to match your interest to the right tech programme",
      "Flexible 3-intake-per-year schedule suits various timelines",
      "SCS has guided multiple students into APU's scholarship track",
    ],
    campusLife: [
      "Purpose-built Tech Hub campus with innovation labs",
      "Microsoft Innovation Centre & Oracle Academy on-site",
      "130+ nationalities creating a global student community",
      "APU Esports Arena and gaming facilities",
      "Active hackathon, startup & tech competition culture",
    ],
  },
};
